import React from "react";
import { AbsoluteFill, Img, staticFile, useCurrentFrame, interpolate, Easing } from "remotion";
import { COLORS, FONT, SAFE } from "../theme";
import { Kicker } from "../components";

/* Escena con FOTO REAL a pantalla completa: Ken Burns + scrim para legibilidad
   + título de marca + barrido de escaneo + chip "VIN registrado". */

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ease = Easing.bezier(0.16, 1, 0.3, 1);

type Props = {
  readonly src: string;
  readonly kicker: string;
  readonly kickerColor?: string;
  readonly title: string;
  readonly origin?: string; // transform-origin del Ken Burns
  readonly scanLabel?: string;
  readonly doneLabel?: string;
};

export const PhotoHero: React.FC<Props> = ({ src, kicker, kickerColor = COLORS.blueLight, title, origin = "60% 55%", scanLabel = "Escaneando QR…", doneLabel = "VIN registrado" }) => {
  const frame = useCurrentFrame();

  const scale = interpolate(frame, [0, 250], [1.06, 1.14], { ...clamp, easing: Easing.linear });
  const fadeIn = interpolate(frame, [0, 14], [0, 1], clamp);

  // Barrido de escáner (una pasada al inicio).
  const sweepY = interpolate(frame, [8, 46], [0, 1080], { ...clamp, easing: Easing.inOut(Easing.quad) });
  const sweepOp = interpolate(frame, [8, 16, 40, 48], [0, 0.9, 0.9, 0], clamp);

  // Chip de estado: "Escaneando" -> "VIN registrado".
  const chipIn = interpolate(frame, [20, 34], [0, 1], { ...clamp, easing: ease });
  const done = frame >= 60;
  const titleOp = interpolate(frame, [16, 34], [0, 1], clamp);
  const titleY = interpolate(frame, [16, 34], [24, 0], { ...clamp, easing: ease });

  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      <AbsoluteFill style={{ opacity: fadeIn }}>
        <Img src={staticFile(src)} style={{ width: "100%", height: "100%", objectFit: "cover", scale: String(scale), transformOrigin: origin }} />
      </AbsoluteFill>

      {/* scrim para legibilidad del texto (arriba-izquierda y base) */}
      <AbsoluteFill style={{ background: "linear-gradient(115deg, rgba(6,12,20,0.82) 0%, rgba(6,12,20,0.32) 42%, rgba(6,12,20,0) 68%)" }} />
      <AbsoluteFill style={{ background: "linear-gradient(0deg, rgba(6,12,20,0.55) 0%, rgba(6,12,20,0) 34%)" }} />

      {/* barrido de escáner */}
      <div style={{ position: "absolute", left: 0, right: 0, top: sweepY, height: 5, background: `linear-gradient(90deg, transparent, ${COLORS.greenLight}, transparent)`, opacity: sweepOp, boxShadow: `0 0 24px ${COLORS.greenLight}` }} />

      {/* título */}
      <div style={{ position: "absolute", left: SAFE, top: SAFE * 0.9, opacity: titleOp, translate: `0px ${titleY}px`, maxWidth: 1000 }}>
        <div style={{ marginBottom: 16 }}><Kicker color={kickerColor}>{kicker}</Kicker></div>
        <div style={{ fontFamily: FONT, fontWeight: 800, fontSize: 82, color: COLORS.white, letterSpacing: -1.8, lineHeight: 1.04, textWrap: "balance", textShadow: "0 4px 24px rgba(0,0,0,0.5)" }}>
          {title}
        </div>
      </div>

      {/* chip de estado (abajo-izquierda) */}
      <div style={{ position: "absolute", left: SAFE, bottom: SAFE * 0.9, opacity: chipIn, display: "flex", alignItems: "center", gap: 14, padding: "14px 22px", borderRadius: 40, background: done ? "rgba(20,42,31,0.72)" : "rgba(10,18,32,0.72)", border: `1px solid ${done ? COLORS.greenLight : COLORS.blueLight}66`, backdropFilter: "blur(6px)" }}>
        {done ? (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={COLORS.greenLight} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.5 2.5L16 9" /></svg>
        ) : (
          <div style={{ width: 26, height: 26, borderRadius: 8, border: `2px solid ${COLORS.blueLight}`, borderTopColor: "transparent", rotate: `${(frame * 12) % 360}deg` }} />
        )}
        <span style={{ fontFamily: FONT, fontWeight: 700, fontSize: 30, color: done ? COLORS.greenLight : COLORS.ink }}>
          {done ? doneLabel : scanLabel}
        </span>
      </div>
    </AbsoluteFill>
  );
};
